import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "customer" | "salon_owner" | "manager" | "admin" | "manager_or_admin";
  redirectTo?: string;
}

/**
 * Protects routes that require authentication and optionally specific roles.
 */
export default function ProtectedRoute({ 
  children, 
  requiredRole,
  redirectTo = "/auth" 
}: ProtectedRouteProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate(redirectTo);
        return;
      }

      if (!requiredRole) {
        setIsAuthorized(true);
        setIsLoading(false);
        return;
      }

      const { data: roles, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);

      if (error) {
        navigate("/");
        return;
      }

      let hasRole = false;
      
      if (requiredRole === "manager_or_admin") {
        // Allow both manager and admin roles
        hasRole = roles?.some(r => r.role === "manager" || r.role === "admin") || false;
      } else {
        hasRole = roles?.some(r => r.role === requiredRole) || false;
      }
      
      if (!hasRole) {
        navigate("/");
        return;
      }

      setIsAuthorized(true);
    } catch {
      navigate(redirectTo);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="flex justify-between items-center">
            <Skeleton className="h-10 w-48" />
            <div className="flex gap-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-24" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-lg border p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return null;
  }

  return <>{children}</>;
}
